/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { FC, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'redux/initialState';

import Button from 'modules/_shared/components/Buttons';
import { setModal } from 'modules/_shared/redux/modalActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { EnvelopeStatus } from 'modules/envelopes/components/EnvelopesTable/envelopeStatus';
import useGetBeneficiariesPendingEnvelopes, {
  BeneficiaryWithEnvelopeId,
} from 'modules/beneficiaries/hooks/useGetBeneficiariesPendingEnvelopes';
import BeneficiariesPendingSignatureToolbar from 'modules/beneficiaries/modals/BeneficiariesPendingSignature/components/BeneficiariesPendingSignatureToolbar';
import BeneficiariesPendingSignatureTable from 'modules/beneficiaries/modals/BeneficiariesPendingSignature/components/BeneficiariesPendingSignatureTable';
import BeneficiariesPendingSignatureTableFilterAlert from 'modules/beneficiaries/modals/BeneficiariesPendingSignature/components/BeneficiariesPendingSignatureTableFilterAlert';

import downloadXLS from 'utils/downloadXLS';

import '../Modals.scss';
import sendReminder from './utils/sendReminder';
import resendInvitation from './utils/resendInvitation';
import {
  BeneficiaryWithEnvelopeDownloadData,
  parseResponseToDownloadData,
} from './utils/parseResponseToDownloadData';

const showHeaders = {
  DOWNLOAD: true,
  SEND_REMINDER: false,
  RESEND_INVITATION: true,
  MIN_DATE: true,
};

const ITEMS_PER_PAGE = 40;

const validStatuses: Record<string, EnvelopeStatus[]> = {
  ALL: [EnvelopeStatus.SENT, EnvelopeStatus.DELIVERED, EnvelopeStatus.VOIDED],
  ONLY_VOIDED: [EnvelopeStatus.VOIDED],
};

type BeneficiariesPendingSignatureProps = {
  beneficiaries: BeneficiaryWithEnvelopeId[];
};

const isValidBeneficiary = (
  beneficiary: BeneficiaryWithEnvelopeId,
  minDate: Date | null
) => {
  const hasValidStatus = validStatuses.ONLY_VOIDED.includes(
    beneficiary.envelopeStatus as EnvelopeStatus
  );

  const hasValidDate = minDate
    ? new Date(beneficiary.planStartDate).getTime() >=
      new Date(minDate).getTime()
    : true;

  return hasValidStatus && hasValidDate;
};

const BeneficiariesPendingSignature: FC<BeneficiariesPendingSignatureProps> = ({
  beneficiaries,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state.user);
  const actualSociety = useSelector(
    (state: State) => state.society.actualSociety
  );

  const [downloadData, setDownloadData] = useState<
    BeneficiaryWithEnvelopeDownloadData[]
  >([]);
  const [minDate, setMinDate] = useState<Date | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [isResendingInvitations, setIsResendingInvitations] = useState(false);
  const [resendingBeneficiaries, setResendingBeneficiaries] = useState<
    Set<string>
  >(new Set());

  // Memoize the filter function to prevent infinite loops
  const filterFn = useMemo(() => {
    if (!minDate) return undefined;
    return (beneficiary: any) =>
      new Date(beneficiary?.planStartDate).getTime() >=
      new Date(minDate).getTime();
  }, [minDate]);

  const {
    data: beneficiariesPendingEnvelopes,
    isLoading,
    updateBeneficiaryStatus,
  } = useGetBeneficiariesPendingEnvelopes({
    beneficiaries,
    currentPage,
    itemsPerPage,
    filterFn,
  });

  // Filter all beneficiaries first to get accurate counts for display
  const allFilteredBeneficiaries =
    beneficiaries?.filter((beneficiary) =>
      minDate
        ? new Date(beneficiary?.planStartDate).getTime() >=
          new Date(minDate).getTime()
        : true
    ) || [];

  // The hook now returns only the current page data, so we use it directly
  const paginatedBeneficiaries = beneficiariesPendingEnvelopes || [];

  const totalBeneficiaries = beneficiaries?.length || 0;
  const filteredCount = allFilteredBeneficiaries.length;
  const isFiltered = minDate !== null;

  const validBeneficiaries = beneficiariesPendingEnvelopes?.filter(
    (beneficiary) => isValidBeneficiary(beneficiary, minDate)
  );

  // Count eligible beneficiaries in current page (those with envelopeId)
  const eligibleBeneficiariesInCurrentPage = validBeneficiaries?.length || 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [minDate]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleDownloadList = () => {
    const downloadFileName = `${t('InvitationLettersPendingOfSignature')}.xlsx`;
    const downloadTabName = format(new Date(), 'yyyy-MM-dd');

    downloadXLS(downloadData, downloadFileName, downloadTabName);
  };

  const handleSendReminder = () => {
    sendReminder({
      t,
      user,
      actualSociety,
      beneficiaries,
    });
  };

  const handleResendInvitation = () => {
    resendInvitation({
      t,
      user,
      actualSociety,
      filteredBeneficiaries: validBeneficiaries,
      setIsResendingInvitations,
      setResendingBeneficiaries,
      updateBeneficiaryStatus,
    });
  };

  useEffect(() => {
    if (beneficiariesPendingEnvelopes?.length) {
      const data = parseResponseToDownloadData(beneficiariesPendingEnvelopes);
      setDownloadData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beneficiariesPendingEnvelopes]);

  return (
    <>
      <Modal.Header>
        <h5 className="modal-title mb-0">
          {totalBeneficiaries} {t('beneficiaries')}
          <span className="text-muted fs-16px ml-2">
            ({validBeneficiaries?.length || 0} {t('availableForResend')})
          </span>
        </h5>

        <a
          className="close cursor-pointer"
          onClick={() => dispatch(setModal(null))}
        >
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>

      <Modal.Body>
        <div className="nk-block">
          <div className="card">
            <BeneficiariesPendingSignatureToolbar
              showHeaders={showHeaders}
              beneficiaries={beneficiaries}
              filteredCount={filteredCount}
              isFiltered={isFiltered}
              isResendingInvitations={isResendingInvitations}
              minDate={minDate}
              eligibleBeneficiariesInCurrentPage={
                eligibleBeneficiariesInCurrentPage
              }
              onDownloadList={handleDownloadList}
              onSendReminder={handleSendReminder}
              onResendInvitation={handleResendInvitation}
              onMinDateChange={setMinDate}
            />

            <div className="card card-bordered">
              <BeneficiariesPendingSignatureTableFilterAlert
                isFiltered={isFiltered}
                filteredCount={filteredCount}
                totalBeneficiaries={totalBeneficiaries}
                onClearFilter={() => setMinDate(null)}
              />

              <BeneficiariesPendingSignatureTable
                paginatedBeneficiaries={paginatedBeneficiaries}
                startIndex={(currentPage - 1) * itemsPerPage}
                filteredCount={filteredCount}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                resendingBeneficiaries={resendingBeneficiaries}
              />
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={() => dispatch(setModal(null))}
          disabled={isResendingInvitations}
        >
          {t('Close')}
        </Button>
      </Modal.Footer>

      {isLoading && <CustomLoading />}
    </>
  );
};

export default BeneficiariesPendingSignature;
